import type { CrudFilter } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import type { OrganizationOut } from '@/api/generated/schemas';
import { SelectFilter } from '@/components/common/SelectFilter';
import type { FilterOption } from '@/components/common/SelectFilter';
import { getFilterValue, type SetFilters } from '@/utils/filters';

type OrganizationSelectFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
};

/**
 * `organization_id[eq]` column filter with server-side name autocomplete —
 * type a name fragment, pick an organization, filter by its id.
 */
export const OrganizationSelectFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
}: OrganizationSelectFilterProps) => {
  const current = getFilterValue<number>(filters, field, 'eq');

  const { selectProps } = useSelect<OrganizationOut>({
    resource: 'tenancy.organizations',
    optionLabel: 'name',
    optionValue: 'id',
    // Hydrates the option label when the filter is already set (e.g. from URL).
    defaultValue: current,
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
  });

  return (
    <SelectFilter<number>
      field={field}
      options={(selectProps.options ?? []) as FilterOption<number>[]}
      filters={filters}
      confirm={confirm}
      clearFilters={clearFilters}
      setFilters={setFilters}
      placeholder={placeholder}
      minWidth={260}
      selectProps={{
        showSearch: true,
        filterOption: false,
        onSearch: selectProps.onSearch,
      }}
    />
  );
};
